import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";

import { enrichmentStatusColumn } from "@/components/common/status-columns";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { IPersonData } from "@/types/person";

export const usePeopleColumns = () =>
  React.useMemo<ColumnDef<IPersonData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        id: "photo",
        header: "",
        accessorFn: (row) => row.document.media?.photos?.[0],
        enableSorting: false,
        enableColumnFilter: false,
        size: 56,
        cell: function render({ getValue }: any) {
          const photo = getValue();
          return photo?.url ? (
            <PhotoWithBlurhash
              className="size-10 rounded-full object-cover"
              url={photo.url}
              blurhash={photo.blurhash ?? ""}
            />
          ) : (
            <div className="size-10 rounded-full bg-muted" />
          );
        },
      },
      {
        id: "name",
        header: "Name",
        minSize: 200,
        size: 280,
        accessorFn: (row) =>
          row.highlights?.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/people/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            />,
          );
        },
        enableSorting: true,
      },
      {
        id: "knownForDepartment",
        header: "Known For",
        accessorFn: (row) => row.document.knownForDepartment ?? "—",
        enableSorting: false,
        size: 150,
        meta: {
          filterType: "faceted-select",
          // Facet counts come back as { value, count }; surface the raw value
          // as both the label and the filter value.
          optionAccessorFn: (facet: any) => ({
            label: facet.value,
            value: facet.value,
          }),
        },
      },
      {
        id: "followers",
        header: "Followers",
        accessorFn: (row) => row.document.followers ?? 0,
        enableSorting: false,
        enableColumnFilter: false,
        size: 100,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        enableSorting: false,
        size: 120,
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
        meta: {
          filterType: "select",
          options: [
            { label: "Draft", value: "Draft" },
            { label: "Complete", value: "Complete" },
            { label: "Archived", value: "Archived" },
          ],
        },
      },
      // Immediately after Status — the shared definition. A person is NOT
      // embeddable, so there is no embedding column.
      enrichmentStatusColumn<IPersonData>(),
      {
        id: "createdAt",
        header: "Created At",
        // See the movies list: `createdAt` is optional on IPerson, so an absent
        // value must read as "—", not as "Invalid Date".
        accessorFn: (row) =>
          row.document.createdAt == null
            ? null
            : parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          const value = getValue();
          if (!value)
            return <span className="text-muted-foreground/60">—</span>;
          return makeTooltip(value.format("DD/MM/YYYY HH:mm"));
        },
        enableColumnFilter: true,
        meta: { filterType: "date" },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText={true}
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [],
  );
